import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDebounceCallback } from 'usehooks-ts';
import { Box, Flex, HStack, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { RiFileList3Line } from 'react-icons/ri';
import moment from 'moment';
import { PageBox } from '../../../../../components/pageBox';
import { Paginator } from '../../../../../components/paginator';
import { ReturnButton } from '../../../../../components/buttons/ReturnButton';
import { MonthNavigator } from '../../../../../components/shared/MonthNavigator';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../../components/SearchModeBar';
import { useMonthFilter } from '../../../../../hooks/useMonthFilter';
import { useNotasAcompanhamento } from '../../../../../hooks/useNotasAcompanhamento';
import { NotasAcompKpiCards } from './components/NotasAcompKpiCards';
import { NotasAcompEmpresaCard } from './components/NotasAcompEmpresaCard';

type SearchMode = 'ID' | 'Nome' | 'CNPJ';

const maskCnpj = (value: string): string => {
  const digits = value
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const PAGE_SIZE = 50;

export function IcmsLivrosFiscaisHomePage() {
  const navigate = useNavigate();
  const { getTotalizadores, getEmpresas } = useNotasAcompanhamento();

  const { currentDate, dateStart, dateEnd, handlePrevMonth, handleNextMonth, handleSelectMonth } =
    useMonthFilter({ defaultToPreviousMonth: true, defaultSize: String(PAGE_SIZE) });

  const [searchParams, setSearchParams] = useSearchParams();
  const searchMode = (searchParams.get('search_mode') as SearchMode) || 'Nome';
  const searchValueParam = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || String(PAGE_SIZE), 10);

  const [searchInput, setSearchInput] = useState(() =>
    searchMode === 'CNPJ' ? maskCnpj(searchValueParam) : searchValueParam,
  );

  useEffect(() => {
    setSearchInput(searchMode === 'CNPJ' ? maskCnpj(searchValueParam) : searchValueParam);
  }, [searchValueParam, searchMode]);

  const debouncedSearch = useDebounceCallback((value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }, 400);

  const handleSearchInputChange = (raw: string) => {
    let next = raw;
    if (searchMode === 'CNPJ') next = maskCnpj(raw);
    else if (searchMode === 'ID') next = raw.replace(/\D/g, '');
    setSearchInput(next);
    debouncedSearch(next);
  };

  const handleSearchModeChange = (mode: SearchMode) => {
    debouncedSearch.cancel?.();
    setSearchInput('');
    const next = new URLSearchParams(searchParams);
    next.set('search_mode', mode);
    next.delete('q');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const totalizadoresFilters = useMemo(
    () => ({ dt_inicio: dateStart, dt_fim: dateEnd }),
    [dateStart, dateEnd],
  );

  const empresasFilters = useMemo(() => {
    const params: Record<string, string | number> = {
      dt_inicio: dateStart,
      dt_fim: dateEnd,
      page,
      size,
    };
    if (searchValueParam) {
      if (searchMode === 'CNPJ')
        params.cnpj = searchValueParam.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      else if (searchMode === 'ID') params.external_id = searchValueParam;
      else params.search = searchValueParam;
    }
    return params;
  }, [dateStart, dateEnd, page, size, searchMode, searchValueParam]);

  const { data: totalizadores, isFetching: isFetchingTotais } = useQuery({
    queryKey: ['notas-acomp-totalizadores', totalizadoresFilters],
    queryFn: getTotalizadores,
    enabled: !!dateStart && !!dateEnd,
    staleTime: 5 * 60 * 1000,
  });

  const empresasQuery = useQuery({
    queryKey: ['notas-acomp-empresas', empresasFilters],
    queryFn: getEmpresas,
    enabled: !!dateStart && !!dateEnd,
    placeholderData: prev => prev,
  });

  const items = empresasQuery.data?.data?.items ?? [];
  const pagination = {
    total: empresasQuery.data?.data?.total ?? 0,
    pages: empresasQuery.data?.data?.pages ?? 0,
    page: empresasQuery.data?.data?.page ?? 1,
    size: empresasQuery.data?.data?.size ?? size,
    next_page: empresasQuery.data?.data?.next_page ?? null,
    previous_page: empresasQuery.data?.data?.previous_page ?? null,
  };

  const isInitialLoading = empresasQuery.isFetching && items.length === 0;
  const isEmpty = !empresasQuery.isFetching && !empresasQuery.isError && items.length === 0;

  const renderList = () => {
    if (isInitialLoading) {
      return (
        <Flex direction="column" align="center" justify="center" py={20} gap={3}>
          <Spinner size="xl" color="primary" />
          <Text fontSize="md" fontWeight="semibold" color="gray.600">
            Carregando empresas...
          </Text>
        </Flex>
      );
    }
    if (empresasQuery.isError && items.length === 0) {
      return (
        <Flex justify="center" py={20}>
          <Text fontSize="md" color="red.500">
            Erro ao carregar empresas. Tente novamente.
          </Text>
        </Flex>
      );
    }
    if (isEmpty) {
      return (
        <Flex justify="center" py={20}>
          <Text fontSize="md" color="gray.500">
            Nenhuma empresa com notas no período.
          </Text>
        </Flex>
      );
    }
    return (
      <>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={3}>
          {items.map(empresa => (
            <NotasAcompEmpresaCard
              key={empresa.id}
              empresa={empresa}
              onClick={emp =>
                navigate(`/modulesHome/supervisor/icms/livros-fiscais/empresa/${emp.external_id}`)
              }
            />
          ))}
        </SimpleGrid>
        {pagination.total > size && <Paginator pagination={pagination} />}
      </>
    );
  };

  return (
    <PageBox>
      <Flex align="center" pb={5} wrap="wrap" gap={2}>
        <ReturnButton previousPage="/modulesHome/supervisor/icms" />
        <Box h="full" flexShrink={0}>
          <Flex align="center" justify="center" borderRadius="full" p={3} bg="#E3F2FD">
            <RiFileList3Line color="#1976D2" className="size-8" />
          </Flex>
        </Box>
        <Flex direction="column" justify="center" gap={1} pl={2} flex="1" minW={0}>
          <Text fontWeight="bold" fontSize={{ base: '14px', md: '16px' }}>
            Livros Fiscais · Acompanhamento
          </Text>
          <Text fontSize={{ base: '11px', md: '12px' }} color="gray.500">
            Empresas com notas no período {moment(dateStart).format('DD/MM/YYYY')} a{' '}
            {moment(dateEnd).format('DD/MM/YYYY')}
          </Text>
        </Flex>
      </Flex>

      <Box mb={4} p={4} bg="gray.50" borderRadius="lg">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={{ base: 3, md: 4 }}
        >
          <HStack spacing={0} flex={1} maxW={{ base: '100%', md: '420px' }}>
            <SearchModeBar
              modes={MODES_ID_NOME_CNPJ}
              mode={searchMode}
              onModeChange={m => handleSearchModeChange(m as SearchMode)}
              value={searchInput}
              onValueChange={handleSearchInputChange}
              inputMode={searchMode === 'ID' ? 'numeric' : 'text'}
              maxLength={searchMode === 'CNPJ' ? 18 : undefined}
            />
          </HStack>

          <HStack spacing={3} flexWrap="wrap" w={{ base: '100%', md: 'auto' }}>
            <Box flex={{ base: 1, md: 'none' }}>
              <MonthNavigator
                currentDate={currentDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
            </Box>
            {(isFetchingTotais || empresasQuery.isFetching) && (
              <Spinner size="sm" color="primary" />
            )}
          </HStack>
        </Flex>
      </Box>

      <NotasAcompKpiCards data={totalizadores} isLoading={isFetchingTotais} />

      {renderList()}
    </PageBox>
  );
}
